import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Box, styled } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';
import { Pathology } from '../classes/pathology';
import { components } from '../utils/markdown';

// Icons for each section
import DescriptionIcon from '@mui/icons-material/Description';
import ScienceIcon from '@mui/icons-material/Science';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import SearchIcon from '@mui/icons-material/Search';
import MedicationIcon from '@mui/icons-material/Medication';
import NotesIcon from '@mui/icons-material/Notes';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  '&:before': {
    display: 'none',
  },
  boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
  },
  color: theme.palette.primary.contrastText,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.contrastText,
}));

export interface PathologySectionProps {
  title: string;
  content?: string;
  icon: React.ReactNode;
  defaultExpanded?: boolean;
}

const PathologySection: React.FC<PathologySectionProps> = ({ 
  title, 
  content, 
  icon, 
  defaultExpanded = false 
}) => {
  // Don't render the section if there's no content
  if (!content) return null;

  return (
    <StyledAccordion defaultExpanded={defaultExpanded}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <IconWrapper>{icon}</IconWrapper>
        <Typography variant="subtitle1" fontWeight="medium">{title}</Typography>
      </StyledAccordionSummary>
      <AccordionDetails>
        <Markdown
          className={'markdown'}
          remarkPlugins={[remarkGfm, supersub]}
          components={components}>
          {content}
        </Markdown>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export interface PathologySectionsProps {
  pathology: Pathology;
}

function PathologySections({ pathology }: PathologySectionsProps) {
  const sections: PathologySectionProps[] = [
    {
      title: "Definizione",
      content: pathology.definition,
      icon: <DescriptionIcon />,
      defaultExpanded: false
    },
    { 
      title: "Eziologia", 
      content: pathology.eziologia, 
      icon: <ScienceIcon />
    },
    { 
      title: "Quadro Clinico", 
      content: pathology.quadroClinico, 
      icon: <MedicalInformationIcon /> 
    },
    { 
      title: "Diagnosi", 
      content: pathology.diagnosi, 
      icon: <SearchIcon /> 
    },
    { 
      title: "Terapia", 
      content: pathology.terapia, 
      icon: <MedicationIcon /> 
    },
    { 
      title: "Note", 
      content: pathology.notes, 
      icon: <NotesIcon /> 
    }
  ];

  return (
    <Box sx={{ mt: 3 }}>
      {sections.map((section) => (
        <PathologySection 
          key={section.title} 
          title={section.title} 
          content={section.content} 
          icon={section.icon}
          defaultExpanded={section.defaultExpanded}
        />
      ))}
    </Box>
  );
}

export default PathologySections;
